import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Truck, PlayCircle, LogIn, ShieldCheck, MapPin, MessageCircle, BarChart3, Users, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { DEMO_SLUG } from "@/lib/tenant";

/**
 * Landing pública — mostrada quando acessam o domínio raiz (`usegestao.app.br`)
 * SEM subdomínio, ou seja, sem contexto de tenant.
 *
 * NÃO faz chamadas à API de dados de empresas — garante isolamento total.
 * Só oferece: (1) botão "Testar Demo" que redireciona para o tenant DEMO,
 * (2) botão "Entrar" que abre a tela de login (para super admins que precisam
 * acessar o painel geral, ou tenant users que sabem o slug).
 */
export default function Landing() {
  const nav = useNavigate();
  const [showLogin, setShowLogin] = useState(false);
  const [loadingDemo, setLoadingDemo] = useState(false);

  const acessarDemo = async () => {
    // Ambiente Demo blindado: chama /auth/demo-login (sem senha) e força reload
    // para /demo/ (window.location) — assim AuthContext relê o cookie novo e
    // Protected deixa passar direto pro dashboard sem passar pelo login.
    setLoadingDemo(true);
    try {
      await api.post("/auth/demo-login");
      window.location.assign(`/${DEMO_SLUG}/`);
    } catch (e) {
      toast.error("Ambiente Demo indisponível — tente em alguns instantes");
    } finally { setLoadingDemo(false); }
  };
  const acessarLogin = () => {
    // Login raiz: pra super admin ou tenant users que sabem o slug depois de logar
    nav("/login");
  };

  const features = [
    { icon: MapPin, title: "Rastreamento GPS", desc: "Localização em tempo real das entregas" },
    { icon: MessageCircle, title: "Chat integrado", desc: "Comunicação direta com entregadores" },
    { icon: Camera, title: "Foto-evidência", desc: "Comprovação visual da entrega concluída" },
    { icon: BarChart3, title: "Relatórios completos", desc: "Faturamento, ticket médio, ranking de entregadores" },
    { icon: Users, title: "Multi-tenant", desc: "Sua farmácia com URL e branding próprios" },
    { icon: ShieldCheck, title: "LGPD compliant", desc: "Cookies httpOnly, RBAC granular, auditoria completa" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-white" data-testid="page-landing">
      {/* Header */}
      <header className="w-full px-6 py-4 border-b border-orange-100 bg-white/70 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Truck className="w-8 h-8 text-orange-600" />
            <span className="text-xl font-extrabold tracking-tight">Controle de Entregas</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={acessarLogin} data-testid="btn-header-login" className="text-orange-700">
              <LogIn className="w-4 h-4 mr-1" />Entrar
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-24 grid md:grid-cols-2 gap-8 items-center">
        <div>
          <div className="inline-block bg-amber-100 text-amber-900 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4">
            SaaS para farmácias e drogarias
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-4">
            Gestão de entregas <span className="text-orange-600">do balcão à porta</span> do cliente
          </h1>
          <p className="text-lg text-muted-foreground mb-6">
            Cadastre entregadores, controle status em tempo real, calcule troco automaticamente e gere relatórios em PDF/CSV. Cada farmácia tem seu próprio subdomínio.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button size="lg" onClick={acessarDemo} data-testid="btn-testar-demo" className="bg-orange-600 hover:bg-orange-700 text-white text-base">
              <PlayCircle className="w-5 h-5 mr-2" />{loadingDemo ? "Carregando demo..." : "Ver Demonstração"}
            </Button>
            <Button size="lg" variant="outline" onClick={acessarLogin} data-testid="btn-hero-login" className="border-orange-300 text-orange-700 hover:bg-orange-50 text-base">
              <LogIn className="w-5 h-5 mr-2" />Já sou cliente
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            💡 Se você já é cliente, acesse pela URL da sua farmácia (ex: <code className="bg-orange-100 px-1.5 py-0.5 rounded">usegestao.app.br/suafarmacia</code>).
          </p>
        </div>

        {/* Visual: card com screenshot mockado */}
        <div className="relative">
          <div className="bg-white rounded-2xl shadow-2xl border-2 border-orange-100 p-6">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b">
              <div className="w-3 h-3 rounded-full bg-rose-400"></div>
              <div className="w-3 h-3 rounded-full bg-amber-400"></div>
              <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
              <span className="text-xs text-muted-foreground ml-2 mono">usegestao.app.br/demo</span>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center text-white font-bold">
                    <Truck className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-bold text-sm">Pedido #4832</div>
                    <div className="text-xs text-muted-foreground">Rua das Flores, 123 · R$ 87,50</div>
                  </div>
                </div>
                <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">Em trânsito</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold">
                    <Truck className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-bold text-sm">Pedido #4831</div>
                    <div className="text-xs text-muted-foreground">Av. Central, 456 · R$ 142,00</div>
                  </div>
                </div>
                <span className="text-xs font-bold text-blue-700 bg-blue-100 px-2 py-0.5 rounded">Aguardando</span>
              </div>
              <div className="grid grid-cols-3 gap-2 pt-2">
                <div className="text-center p-2 bg-orange-50 rounded">
                  <div className="text-lg font-extrabold text-orange-600">28</div>
                  <div className="text-[10px] text-muted-foreground uppercase">Hoje</div>
                </div>
                <div className="text-center p-2 bg-emerald-50 rounded">
                  <div className="text-lg font-extrabold text-emerald-600">R$ 3.2k</div>
                  <div className="text-[10px] text-muted-foreground uppercase">Faturamento</div>
                </div>
                <div className="text-center p-2 bg-blue-50 rounded">
                  <div className="text-lg font-extrabold text-blue-600">R$ 114</div>
                  <div className="text-[10px] text-muted-foreground uppercase">Ticket médio</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section className="max-w-6xl mx-auto px-6 py-12 md:py-16">
        <h2 className="text-3xl md:text-4xl font-extrabold text-center mb-10">Tudo que sua farmácia precisa</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f) => (
            <div key={f.title} className="p-5 border border-orange-100 rounded-xl bg-white hover:border-orange-300 transition" data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <f.icon className="w-8 h-8 text-orange-600 mb-3" />
              <h3 className="font-bold mb-1">{f.title}</h3>
              <p className="text-sm text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="max-w-6xl mx-auto px-6 py-16 text-center">
        <h2 className="text-3xl md:text-4xl font-extrabold mb-4">Pronto para experimentar?</h2>
        <p className="text-muted-foreground mb-6">Ambiente demo com dados reais para você navegar sem cadastro.</p>
        <Button size="lg" onClick={acessarDemo} data-testid="btn-footer-demo" className="bg-orange-600 hover:bg-orange-700 text-white text-base">
          <PlayCircle className="w-5 h-5 mr-2" />Acessar o ambiente Demo
        </Button>
      </section>

      <footer className="border-t border-orange-100 bg-white/50 text-center py-6 text-xs text-muted-foreground">
        © 2026 UseGestão · SaaS de gestão de entregas
      </footer>
    </div>
  );
}
